import { SWRConfiguration, mutate } from 'swr';

import {
  UseSWRMutationOptions,
  matchKeyPartial,
  useSWR,
  useSWRMutation,
} from '@/hooks/useSwr';
import {
  apiSessionsPath,
  appInsightsConfigsPath,
  appInsightsPath,
} from '@/utils/constants';

import {
  CreateInsightsJobConfigRequest,
  GetInsightsJobConfigResponse,
  InsightsJobConfigWithId,
  UpdateInsightsJobConfigRequest,
} from '../../../types';

export const BASE_INSIGHTS_JOB_CONFIGS_URL = (sessionId: string) =>
  `${apiSessionsPath}/${sessionId}/${appInsightsPath}/${appInsightsConfigsPath}`;

export function useInsightsJobConfigs({
  sessionId,
  includePrebuilts,
  options,
}: {
  sessionId?: string;
  includePrebuilts?: boolean;
  options?: SWRConfiguration;
}) {
  return useSWR<GetInsightsJobConfigResponse>(
    sessionId
      ? {
          url: `${BASE_INSIGHTS_JOB_CONFIGS_URL(sessionId)}${
            includePrebuilts ? '?include_prebuilts=true' : ''
          }`,
        }
      : null,
    options
  );
}

export function useCreateInsightsJobConfig({
  sessionId,
  options,
}: {
  sessionId: string;
  options?: UseSWRMutationOptions<
    InsightsJobConfigWithId,
    CreateInsightsJobConfigRequest
  >;
}) {
  return useSWRMutation<
    InsightsJobConfigWithId,
    CreateInsightsJobConfigRequest
  >(
    {
      url: BASE_INSIGHTS_JOB_CONFIGS_URL(sessionId),
      method: 'POST',
    },
    {
      ...options,
      onSuccess: async (...props) => {
        // Invalidate all configs cache keys (with and without query params)
        await mutate(
          matchKeyPartial({
            url: `${BASE_INSIGHTS_JOB_CONFIGS_URL(
              sessionId
            )}?include_prebuilts=true`,
          })
        );
        options?.onSuccess?.(...props);
      },
    }
  );
}

export function useUpdateInsightsJobConfig({
  sessionId,
  configId,
  options,
}: {
  sessionId: string;
  configId?: string;
  options?: UseSWRMutationOptions<
    GetInsightsJobConfigResponse,
    UpdateInsightsJobConfigRequest
  >;
}) {
  return useSWRMutation<
    GetInsightsJobConfigResponse,
    UpdateInsightsJobConfigRequest
  >(
    {
      url: configId
        ? BASE_INSIGHTS_JOB_CONFIGS_URL(sessionId) + `/${configId}`
        : '',
      method: 'PATCH',
    },
    {
      ...options,
      onSuccess: async (...props) => {
        // Invalidate all configs cache keys (with and without query params)
        const baseUrl = BASE_INSIGHTS_JOB_CONFIGS_URL(sessionId);
        await Promise.all([
          mutate(
            matchKeyPartial({ url: configId ? `${baseUrl}/${configId}` : '' })
          ),
          mutate(matchKeyPartial({ url: `${baseUrl}?include_prebuilts=true` })),
        ]);
        options?.onSuccess?.(...props);
      },
    }
  );
}
